import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Globe } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { lang, toggleLang, t } = useLang();

  const navLinks = [
    { label: t("nav.home"), path: "/" },
    { label: t("nav.plots"), path: "/plots" },
    { label: "360° Tour", path: "/virtual-tour" },
    { label: t("nav.gallery"), path: "/gallery" },
    { label: t("nav.schemes"), path: "/schemes" },
    { label: t("nav.finance"), path: "/finance" },
    { label: t("nav.booking"), path: "/booking" },
    { label: t("nav.calculator"), path: "/calculator" },
    { label: "Blog", path: "/blog" },
    { label: t("nav.contact"), path: "/contact" },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const isHome = location.pathname === "/";

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || !isHome || menuOpen
          ? "glass shadow-lg"
          : "bg-transparent"
      }`}
    >
      {/* Top bar */}
      <div className="bg-orange-600/90 backdrop-blur-sm text-white text-xs py-1.5 px-4 text-center font-medium tracking-wide hidden md:block">
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", letterSpacing: "0.15em" }}>
          {t("topbar.location")} &nbsp;|&nbsp;
          <Phone className="inline w-3 h-3 mr-1" />
          +91-9258959058 &nbsp;|&nbsp; amritinfotech9@gmail.com
        </span>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="navbar-logo">
            <div className="w-9 h-9 bg-orange-500 flex items-center justify-center font-black text-white text-xs transition-transform group-hover:scale-110"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.02em" }}>
              VS
            </div>
            <div>
              <div
                className="text-white text-base leading-none"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em" }}
              >
                VS Agro Infra Enterprises
              </div>
              <div className="text-orange-400 text-xs leading-none font-medium" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", letterSpacing: "0.15em" }}>
                VSINDLPARK.COM
              </div>
            </div>
          </Link>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
                className={`relative px-3 py-2 text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? "text-orange-400"
                    : "text-slate-300 hover:text-orange-400"
                }`}
              >
                {link.label}
                {location.pathname === link.path && (
                  <motion.div
                    layoutId="nav-underline"
                    className="absolute bottom-0 left-3 right-3 h-0.5 bg-orange-500"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>
            ))}
          </div>

          {/* CTA + Lang Toggle */}
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={toggleLang}
              data-testid="lang-toggle-btn"
              className="flex items-center gap-1.5 text-slate-300 text-sm hover:text-orange-400 transition-colors border border-slate-600 hover:border-orange-500 px-2.5 py-1.5"
              title={lang === "en" ? "Switch to Hindi" : "Switch to English"}
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              <Globe className="w-3.5 h-3.5" />
              <span className="font-bold text-xs">{lang === "en" ? "HI" : "EN"}</span>
            </button>
            <a
              href="tel:+919258959058"
              className="flex items-center gap-1.5 text-slate-300 text-sm hover:text-orange-400 transition-colors"
            >
              <Phone className="w-4 h-4" />
            </a>
            <Link
              to="/booking"
              data-testid="navbar-book-btn"
              className="relative overflow-hidden bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 text-sm font-bold tracking-wider uppercase transition-all duration-300 hover:shadow-[0_0_20px_rgba(249,115,22,0.4)]"
            >
              {t("nav.bookNow")}
            </Link>
          </div>

          {/* Mobile: Lang + hamburger */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={toggleLang}
              data-testid="lang-toggle-btn-mobile"
              className="text-white border border-slate-600 px-2 py-1.5 flex items-center gap-1"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              <Globe className="w-3.5 h-3.5" />
              <span className="text-xs font-bold">{lang === "en" ? "HI" : "EN"}</span>
            </button>
            <button
              className="text-white p-2"
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="navbar-mobile-menu-btn"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden pb-4 border-t border-white/10 mt-2 overflow-hidden"
            >
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link
                    to={link.path}
                    className={`block px-4 py-3 text-sm font-medium border-b border-white/5 ${
                      location.pathname === link.path ? "text-orange-400" : "text-slate-300"
                    }`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              <Link
                to="/booking"
                className="block mt-3 mx-4 bg-orange-500 text-white text-center py-2.5 text-sm font-bold uppercase tracking-wider"
              >
                {t("nav.bookPlotNow")}
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
}
